import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import CompanyForm from '../components/CompanyForm';
import { confirmDialog } from '../utils/confirm';
import { useAppContext } from '../contexts/AppContext';

const Companies = () => {
  const { isAdmin } = useAppContext();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const res = await apiEndpoints.companies.list();
        setCompanies(res.data);
        setFilteredCompanies(res.data);
      } catch (err) {
        console.error('Failed to load companies:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      const filtered = companies.filter((c) => {
        return [c.label, c.key, c.contact, c.email, c.phone]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
      setFilteredCompanies(filtered);
    } else {
      setFilteredCompanies(companies);
    }
  }, [searchTerm, companies]);

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
  };

  const handleBack = () => {
    setSelectedCompany(null);
  };

  const refreshCompanies = async () => {
    const res = await apiEndpoints.companies.list();
    setCompanies(res.data);
    setFilteredCompanies(res.data);
  };

  if (loading) return <div className="page-loading">Loading companies...</div>;

  if (selectedCompany) {
    const companyTitle = selectedCompany.label || selectedCompany.key || 'Company';
    return (
      <div>
        <button className="back-btn" onClick={handleBack}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to companies
        </button>

        <div className="card">
          <div className="card-header">
            <div className="card-title">{companyTitle}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {isAdmin && <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setEditingCompany(selectedCompany); setShowCompanyForm(true); }}
              >
                Edit
              </button>}
              {isAdmin && <button
                className="btn btn-danger btn-sm"
                onClick={async () => {
                  if (!await confirmDialog('Are you sure you want to delete this company?')) return;
                  try {
                    await apiEndpoints.companies.delete(selectedCompany.key);
                    setSelectedCompany(null);
                    const res = await apiEndpoints.companies.list();
                    setCompanies(res.data);
                  } catch (err) {
                    console.error('Failed to delete company:', err);
                    alert('Error deleting company. Please try again.');
                  }
                }}
              >
                Delete
              </button>}
            </div>
          </div>
          <div className="card-body">
            <div className="info-row">
              <div className="info-lbl">Key</div>
              <div className="info-val">{selectedCompany.key}</div>
            </div>
            <div className="info-row">
              <div className="info-lbl">Contact</div>
              <div className="info-val">{selectedCompany.contact || '—'}</div>
            </div>
            <div className="info-row">
              <div className="info-lbl">Address</div>
              <div className="info-val">{selectedCompany.address || '—'}</div>
            </div>
            <div className="info-row">
              <div className="info-lbl">Phone / Email</div>
              <div className="info-val">{selectedCompany.phone || '—'} / {selectedCompany.email || '—'}</div>
            </div>
          </div>
        </div>

        <CompanyForm
          isOpen={showCompanyForm}
          onClose={() => setShowCompanyForm(false)}
          company={editingCompany}
          onSave={async (formData) => {
            const payload = {
              label: formData.name,
              contact: formData.contact,
              address: formData.address,
              phone: formData.phone,
              email: formData.email,
            };
            try {
              if (editingCompany) {
                await apiEndpoints.companies.update(editingCompany.key, payload);
              } else {
                await apiEndpoints.companies.create(payload);
              }
              const res = await apiEndpoints.companies.list();
              setCompanies(res.data);
              setSelectedCompany(null);
              setShowCompanyForm(false);
            } catch (err) {
              console.error('Failed to save company:', err);
              alert('Error saving company. Please try again.');
            }
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Company</div>
          <div className="ph-sub">{companies.length} partner companies</div>
        </div>
        {isAdmin && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => { setEditingCompany(null); setShowCompanyForm(true); }}
          >
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Company
          </button>
        )}
      </div>

      <div className="table-filters">
        <div className="filter-row">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8.5" cy="8.5" r="5.5" />
              <path d="M13.5 13.5L18 18" />
            </svg>
            <input
              type="search"
              placeholder="Search for company, contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body vehicle-table-card">
          <table className="vehicle-table">
            <thead>
              <tr>
                <th>COMPANY NAME</th>
                <th>CONTACT PERSON</th>
                <th>PHONE</th>
                <th>EMAIL</th>
                <th>TYPE</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-row">
                    No companies match the search.
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company, index) => {
                  const companyName = company.label || company.key || 'Company';
                  const companyType = company.type || 'Standard';

                  return (
                    <tr key={company._id || index} onClick={() => handleSelectCompany(company)}>
                      <td className="cell-title">
                        <div className="vehicle-name">{companyName}</div>
                        <div className="vehicle-sub">{company.key}</div>
                      </td>
                      <td data-label="Contact Person">{company.contact || '—'}</td>
                      <td data-label="Phone">{company.phone || '—'}</td>
                      <td data-label="Email">{company.email || '—'}</td>
                      <td data-label="Type">{companyType}</td>
                      <td className="row-action cell-action">
                        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); handleSelectCompany(company); }}>
                          Profile →
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CompanyForm
        isOpen={showCompanyForm}
        onClose={() => setShowCompanyForm(false)}
        company={editingCompany}
        onSave={async (formData) => {
          const payload = {
            label: formData.name,
            contact: formData.contact,
            address: formData.address,
            phone: formData.phone,
            email: formData.email,
          };
          try {
            if (editingCompany) {
              await apiEndpoints.companies.update(editingCompany.key, payload);
            } else {
              await apiEndpoints.companies.create(payload);
            }
            await refreshCompanies();
            setShowCompanyForm(false);
          } catch (err) {
            console.error('Failed to save company:', err);
            alert('Error saving company. Please try again.');
          }
        }}
      />
    </div>
  );
};

export default Companies;
